import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { HelmetProvider, Helmet } from 'react-helmet-async'
import { Phone, Mail } from 'lucide-react'
import ScrollToTop from './components/ScrollToTop.jsx'
import Header from './components/Header.jsx'
import Footer from './components/Footer.jsx'
import PageTemplate from './pages/PageTemplate.jsx'

export default function App() {
  const [settings, setSettings] = React.useState(null)

  React.useEffect(() => {
    let isMounted = true
    const loadSettings = async () => {
      try {
        const res = await fetch('/_api/public/content/generelle_indstillinger')
        if (!res.ok) return
        const data = await res.json()
        const items = Array.isArray(data) ? data : (data.items || [])
        if (items.length > 0 && isMounted) {
          setSettings(items[0])
        }
      } catch (e) {
        // Fallback
      }
    }
    loadSettings()
    return () => { isMounted = false }
  }, [])

  const phone = settings?.telefon || '+45 20 53 27 67'
  const email = settings?.email || 'dvbolig@hotmail.com'

  return (
    <HelmetProvider>
      <div className="flex min-h-screen flex-col bg-white text-stone-800 antialiased">
        {/* Scroll Restoration */}
        <ScrollToTop />

        {/* Header */}
        <Header />

        {/* Sticky Vertical Toolbar */}
        <div className="fixed top-1/3 right-0 z-50 flex flex-col gap-0.5" role="region" aria-label="Hurtig kontakt">
          <a
            href={`tel:${phone.replace(/\s+/g, '')}`}
            className="w-12 h-12 flex items-center justify-center bg-[#c08f66] text-white hover:bg-[#a6764e] transition-colors shadow-md"
            title="Ring til os"
            aria-label={`Ring til os på ${phone}`}
          >
            <Phone className="h-5 w-5" />
          </a>
          <a
            href={`mailto:${email}`}
            className="w-12 h-12 flex items-center justify-center bg-[#c08f66] text-white hover:bg-[#a6764e] transition-colors shadow-md"
            title="Send e-mail"
            aria-label={`Send e-mail til ${email}`}
          >
            <Mail className="h-5 w-5" />
          </a>
        </div>

        {/* Main Content Area */}
        <main className="flex-grow">
          <Routes>
            {/* Main Pages */}
            <Route path="/" element={<PageTemplate key="index" pageKey="index" />} />
            <Route path="/toemrerarbejde" element={<PageTemplate key="toemrerarbejde" pageKey="toemrerarbejde" />} />
            <Route path="/renovering" element={<PageTemplate key="renovering" pageKey="renovering" />} />
            <Route path="/tagarbejde" element={<PageTemplate key="tagarbejde" pageKey="tagarbejde" />} />
            <Route path="/nybyg-og-tilbygning" element={<PageTemplate key="nybyg-og-tilbygning" pageKey="nybyg-og-tilbygning" />} />
            <Route path="/referencer" element={<PageTemplate key="referencer" pageKey="referencer" />} />
            <Route path="/om-os" element={<PageTemplate key="om-os" pageKey="om-os" />} />
            <Route path="/kontakt" element={<PageTemplate key="kontakt" pageKey="kontakt" />} />

            {/* Sub-services under Tømrerarbejde */}
            <Route path="/vinduer-og-doere" element={<PageTemplate key="vinduer-og-doere" pageKey="vinduer-og-doere" />} />
            <Route path="/traeterasse" element={<PageTemplate key="traeterasse" pageKey="traeterasse" />} />
            <Route path="/gulve-og-lofter" element={<PageTemplate key="gulve-og-lofte" pageKey="gulve-og-lofte" />} />
            <Route path="/hegn-og-stakit" element={<PageTemplate key="hegn-og-stakit" pageKey="hegn-og-stakit" />} />

            {/* Redirects */}
            <Route path="/toemrer-i-mors" element={<Navigate to="/" replace />} />
            <Route path="/toemrer-i-mors/*" element={<Navigate to="/" replace />} />

            {/* Fallback 404 Route */}
            <Route
              path="*"
              element={
                <>
                  <Helmet>
                    <title>404 - Siden blev ikke fundet | Tømrermester Daniel Vandet</title>
                    <meta name="robots" content="noindex, nofollow" />
                  </Helmet>
                  <div className="max-w-3xl mx-auto px-4 py-24 text-center">
                    <h1 className="text-4xl font-extrabold text-[#1e1e1e] mb-4">404 - Siden blev ikke fundet</h1>
                    <p className="text-[#475569] mb-8">
                      Beklager, men vi kunne ikke finde den side, du leder efter.
                    </p>
                    <a
                      href="/"
                      className="inline-flex items-center justify-center bg-[#c08f66] text-white font-medium px-6 py-3 rounded-sm hover:bg-[#a6764e] transition-colors duration-200 text-sm"
                    >
                      Gå til forsiden
                    </a>
                  </div>
                </>
              }
            />
          </Routes>
        </main>

        {/* Footer */}
        <Footer />
      </div>
    </HelmetProvider>
  )
}
